import { get } from 'svelte/store';
import { getDateByTimeZone } from 'src/helpers/date_helper';
import type {
	ChartControlsDataInterface,
	RangeOptionInterface,
} from 'src/helpers/statistics/ui/chart_control_helpers';
import { user_data } from 'src/stores/account/user';

const nowInUserTimezone = (): Date => getDateByTimeZone(get(user_data)?.timezone);

/** Inclusive API range: Jan 1 → today (user timezone). */
export const getHeatmapYearRange = (now: Date = nowInUserTimezone()): { from: Date; to: Date } => {
	const from = new Date(now.getFullYear(), 0, 1);
	from.setHours(0, 0, 0, 0);
	const to = new Date(now);
	to.setHours(23, 59, 59, 999);
	return { from, to };
};

/** Calendar grid bounds for the current year (`end` is exclusive). */
export const getHeatmapCalendarBounds = (
	now: Date = nowInUserTimezone(),
): { start: Date; end: Date } => ({
	start: new Date(now.getFullYear(), 0, 1),
	end: new Date(now.getFullYear() + 1, 0, 1),
});

export const getHeatmapMonthRange = (now: Date = nowInUserTimezone()): { from: Date; to: Date } => {
	const from = new Date(now.getFullYear(), now.getMonth(), 1);
	from.setHours(0, 0, 0, 0);
	const to = new Date(now);
	to.setHours(23, 59, 59, 999);
	return { from, to };
};

const getHeatmapDayRange = (
	dayOffset: number,
	now: Date = nowInUserTimezone(),
): { from: Date; to: Date } => {
	const from = new Date(now);
	from.setDate(from.getDate() + dayOffset);
	from.setHours(0, 0, 0, 0);
	const to = new Date(from);
	to.setHours(23, 59, 59, 999);
	return { from, to };
};

export const buildHeatmapYearControls = (): ChartControlsDataInterface => {
	const time = getHeatmapYearRange();
	const rangeOption = {
		period: 'year',
		timeRange: '1y',
		timeLabelFormat: 'year',
		enabled: true,
	} as RangeOptionInterface;

	return {
		time,
		format: 'Y-m-d H:i:s',
		rangeOption,
		currentPeriod: 'year',
	};
};

export const buildHeatmapMonthControls = (): ChartControlsDataInterface => ({
	time: getHeatmapMonthRange(),
	format: 'Y-m-d H:i:s',
	rangeOption: {
		period: 'day',
		timeRange: '1m',
		timeLabelFormat: 'month',
		enabled: true,
	} as RangeOptionInterface,
	currentPeriod: 'month',
});

const buildHeatmapDayControls = (dayOffset: number): ChartControlsDataInterface => ({
	time: getHeatmapDayRange(dayOffset),
	format: 'Y-m-d H:i:s',
	rangeOption: {
		period: 'hour',
		timeRange: '1d',
		timeLabelFormat: 'day',
		enabled: true,
	} as RangeOptionInterface,
	currentPeriod: '24_hours',
});

export const buildHeatmapTodayControls = (): ChartControlsDataInterface => buildHeatmapDayControls(0);

export const buildHeatmapYesterdayControls = (): ChartControlsDataInterface =>
	buildHeatmapDayControls(-1);
